import React, { FC, PropsWithChildren } from 'react';
import { genericMemo } from '@direct-frontend/stdlib';

import { Badge } from '../../Badge';
import './SidebarNotification.css';
import { cn } from '../../../lib/css/className/className';
import { HStack } from '../../Stack';
import { getSizeSkin } from '../../../lib/skin/getSizeSkin/getSizeSkin';
import { TestProps } from '../../../internal/types/testTypes';

export type SidebarNotificationProps = PropsWithChildren<
    TestProps & {
        counter?: number;
        collapsed?: boolean;
        isTruncated?: boolean;
        color?: 'info' | 'error';
    }
>;

const cnSidebarNotification = cn('SidebarNotification');

export const SidebarNotification: FC<SidebarNotificationProps> = genericMemo(function SidebarNotification({
    children,
    counter,
    collapsed,
    isTruncated,
    color = 'info',
    'data-testid': dataTestId = 'SidebarNotification',
}) {
    let handledCounter;

    if (counter) {
        handledCounter = Math.min(counter, 99);
    }

    const sizeSkinClass = getSizeSkin('s');

    const content = collapsed ? (
        <HStack className={cnSidebarNotification('')}>
            {children}

            <Badge className={cnSidebarNotification('badge')} color={color} size="16">
                {handledCounter}
            </Badge>
        </HStack>
    ) : (
        <HStack alignItems="center" gap="current-help" className={cnSidebarNotification({ truncated: isTruncated })}>
            {children}

            <Badge className={cnSidebarNotification({ empty: !handledCounter })} color={color} size="16">
                {handledCounter}
            </Badge>
        </HStack>
    );

    return (
        <HStack
            alignItems="center"
            className={cnSidebarNotification('layout', { expanded: !collapsed, truncated: isTruncated }, [
                sizeSkinClass,
            ])}
            data-testid={dataTestId}
        >
            {content}
        </HStack>
    );
});
